
import React, { createContext } from 'react';
import { initializeApp, getApp, getApps } from "firebase/app";
import { getDatabase, ref, query, orderByChild, equalTo, limitToLast, serverTimestamp } from "firebase/database";

// S4 (2026-05-26): admin bookingListRef returns the last N records by key (Firebase push
// keys sort lexicographically by creation time, so this is approximately "newest first").
// 500 covers typical recent-bookings admin views. Earnings totals computed from the bookings
// list are now bounded by this slice — true lifetime totals should aggregate server-side
// (tracked as a follow-up to S4). Increase before adding load-more UI if admins report
// missing records.
const ADMIN_BOOKINGS_FETCH_LIMIT = 500;
import { getFirestore, collection, doc } from "firebase/firestore";
import { initializeAuth, getAuth, GoogleAuthProvider, OAuthProvider, signInWithPhoneNumber, PhoneAuthProvider, RecaptchaVerifier, unlink, updatePhoneNumber, linkWithPhoneNumber, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { getStorage, ref as stRef } from "firebase/storage";

const FirebaseContext = createContext(null);

let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
    firestore: null,
}   

const createFullStructure = (app, db, auth, storage, firestore, config) => {
    return {
        app: app,
        config: config,
        database: db,
        auth: auth,
        storage: storage,
        firestore: firestore,
        serverTimestamp: () => serverTimestamp(),
        authRef:getAuth,
        googleProvider:new GoogleAuthProvider(),
        appleProvider:new OAuthProvider('apple.com'),
        phoneProvider:new PhoneAuthProvider(auth),          
        RecaptchaVerifier: RecaptchaVerifier,
        signInWithPhoneNumber: signInWithPhoneNumber,   
        updatePhoneNumber:updatePhoneNumber,
        unlink: unlink,
        googleCredential: (idToken, accessToken)=> GoogleAuthProvider.credential(idToken, accessToken),    
        linkWithPhoneNumber: linkWithPhoneNumber,
        mobileAuthCredential: (verificationId,code) => PhoneAuthProvider.credential(verificationId, code),           
        usersRef: ref(db, 'users'),
        bookingRef: ref(db, 'bookings'),
        cancelreasonRef: ref(db, 'cancel_reason'),
        settingsRef: ref(db, 'settings'),
        smtpRef: ref(db, "smtpdata"),
        smsRef: ref(db, "smsConfig"),
        carTypesRef: ref(db, 'cartypes'),
        carTypesEditRef:(id) => ref(db, "cartypes/"+ id),
        carDocImage:(id) =>  stRef(storage, `cartypes/${id}`),     
        promoRef: ref(db, 'promos'),
        promoEditRef:(id) => ref(db, "promos/"+ id),
        notifyRef: ref(db,"notifications/"),
        notifyEditRef:(id) => ref(db, "notifications/"+ id),
        addressRef: (uid,id) =>  ref(db, "savedAddresses/"+ uid + "/" + id),
        addressEditRef:(uid) => ref(db, "savedAddresses/"+ uid),
        singleUserRef:(uid) => ref(db, "users/" + uid),
        profileImageRef:(uid) => stRef(storage,`users/${uid}/profileImage`),
        verifyIdImageRef:(uid) => stRef(storage,`users/${uid}/verifyIdImage`),
        taxPlateRef:(uid) => stRef(storage,`users/${uid}/taxPlate`),
        bookingImageRef:(bookingId,imageType) => stRef(storage,`bookings/${bookingId}/${imageType}`),
        driversRef: query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")),
        driverDocsRef:(uid) => stRef(storage,`users/${uid}/license`),       
        driverDocsRefBack:(uid) => stRef(storage,`users/${uid}/licenseBack`),         
        singleBookingRef:(bookingKey) => ref(db, "bookings/" + bookingKey),
        requestedDriversRef:(bookingKey ) => ref(db, "bookings/" + bookingKey  + "/requestedDrivers"),
        referralIdRef:(referralId) => query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
        trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
        tasksRef:() => query(ref(db, 'bookings'), orderByChild('status'), equalTo('NEW')),
        singleTaskRef:(uid,bookingId) => ref(db, "bookings/" + bookingId  + "/requestedDrivers/" + uid),
        bookingListRef:(uid,role) =>
            role == 'customer'? query(ref(db, 'bookings'), orderByChild('customer'), equalTo(uid)):
                (role == 'driver'?
                    query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid))
                    :
                    (role == 'fleetadmin'?
                        query(ref(db, 'bookings'), orderByChild('fleetadmin'), equalTo(uid))
                        // Admin path: capped to most-recent N by push key. See ADMIN_BOOKINGS_FETCH_LIMIT.
                        : query(ref(db, 'bookings'), limitToLast(ADMIN_BOOKINGS_FETCH_LIMIT))
                    )
                ),
        chatRef:(bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
        withdrawRef: ref(db, 'withdraws/'),
        // Account deletion is admin-gated: users write a request here instead of
        // removing users/{uid} themselves (which would fire the userDelete trigger).
        deletionRequestsRef: ref(db, 'deletionRequests'),
        singleDeletionRequestRef:(uid) => ref(db, "deletionRequests/" + uid),
        campaignsRef: ref(db, 'campaigns'),
        campaignEditRef:(id) => ref(db, "campaigns/" + id),
        languagesRef: ref(db, "languages"),
        languagesEditRef:(id) => ref(db, "languages/"+ id),
        walletHistoryRef:(uid) => ref(db, "walletHistory/" + uid),  
        userNotificationsRef:(uid) =>  ref(db, "userNotifications/"+ uid),
        userRatingsRef:(uid) =>  ref(db, "userRatings/"+ uid),
        carsRef:(uid,role) => role == 'driver'? 
            query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid))
            :(role == 'fleetadmin'? 
                query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
                : ref(db, 'cars')
            ),
        carAddRef: ref(db, "cars"),
        carEditRef:(id) => ref(db, "cars/"+ id),
        carImage:(id) => stRef(storage,`cars/${id}`),   
        allLocationsRef: ref(db, "locations"),
        userLocationRef:(uid) => ref(db, "locations/"+ uid),
        sosRef: ref(db, 'sos'),
        editSosRef:(id) => ref(db, "sos/" +id),
        complainRef: ref(db, 'complain'),
        editComplainRef:(id) => ref(db, "complain/" +id),
        paymentSettingsRef: ref(db, "payment_settings"),
        usedreferralRef:ref(db,'usedreferral'),
        // İYS Consent References - Yeni yapı için güncellendi
        consentRef: (uid) => ref(db, `consents/${uid}`),
    }
}

const FirebaseProvider  = ({ config, children, AsyncStorage, token }) => {
    let app, auth, database, storage, firestore;

    if (!getApps().length) {
        app = initializeApp(config);
    } else {
        app = getApp();
    }

    try {
        if (typeof document !== 'undefined') {
            auth = initializeAuth(app, {
                persistence: browserLocalPersistence,
                popupRedirectResolver: browserPopupRedirectResolver,
            });
        } else {
            try {
                const authModule = require("firebase/auth");
                if (authModule.getReactNativePersistence) {
                    auth = initializeAuth(app, {
                        persistence: authModule.getReactNativePersistence(AsyncStorage),
                    });
                } else {
                    auth = getAuth(app);
                }
            } catch (e) {
                // Auth already initialized (e.g. by @react-native-firebase)
                auth = getAuth(app);
            }
        }
    } catch (error) {
        auth = getAuth(app);
    }

    database = getDatabase(app);
    storage = getStorage(app);
    firestore = getFirestore(app);

    firebase = createFullStructure(app, database, auth, storage, firestore, config);

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    )
}

export {
    firebase,
    FirebaseProvider,
    FirebaseContext
}
